const ALLOWED_TAGS = new Set([
  "a",
  "b",
  "blockquote",
  "br",
  "div",
  "em",
  "font",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "i",
  "li",
  "ol",
  "p",
  "s",
  "span",
  "strike",
  "strong",
  "sub",
  "sup",
  "u",
  "ul",
]);

const REMOVE_WITH_CONTENT_TAGS = new Set([
  "iframe",
  "link",
  "meta",
  "object",
  "script",
  "style",
]);

const ALLOWED_STYLE_PROPERTIES = new Set([
  "background-color",
  "color",
  "font-family",
  "font-size",
  "font-style",
  "font-weight",
  "text-align",
  "text-decoration",
]);

function sanitizeStyleAttribute(styleValue = "") {
  return String(styleValue || "")
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const [property = "", ...rest] = rule.split(":");
      const normalizedProperty = property.trim().toLowerCase();
      const value = rest.join(":").trim();

      if (!ALLOWED_STYLE_PROPERTIES.has(normalizedProperty) || !value) {
        return null;
      }

      const normalizedValue = value.toLowerCase();
      if (
        normalizedValue.includes("expression") ||
        normalizedValue.includes("javascript:") ||
        normalizedValue.includes("url(")
      ) {
        return null;
      }

      return `${normalizedProperty}: ${value}`;
    })
    .filter(Boolean)
    .join("; ");
}

function unwrapNode(node) {
  const parent = node.parentNode;
  if (!parent) {
    return;
  }

  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
}

function sanitizeElementAttributes(element) {
  const tag = element.tagName.toLowerCase();
  const attributes = [...element.attributes];

  attributes.forEach((attribute) => {
    const name = attribute.name.toLowerCase();
    const value = attribute.value;

    if (name.startsWith("on")) {
      element.removeAttribute(attribute.name);
      return;
    }

    if (name === "style") {
      const sanitizedStyle = sanitizeStyleAttribute(value);
      if (sanitizedStyle) {
        element.setAttribute("style", sanitizedStyle);
      } else {
        element.removeAttribute("style");
      }
      return;
    }

    if (tag === "a" && name === "href") {
      const href = String(value || "").trim();
      const normalizedHref = href.toLowerCase();
      const isSafeHref =
        normalizedHref.startsWith("http://") ||
        normalizedHref.startsWith("https://") ||
        normalizedHref.startsWith("mailto:") ||
        normalizedHref.startsWith("tel:") ||
        normalizedHref.startsWith("#") ||
        normalizedHref.startsWith("/");

      if (!isSafeHref) {
        element.removeAttribute("href");
      } else {
        element.setAttribute("href", href);
        element.setAttribute("target", "_blank");
        element.setAttribute("rel", "noreferrer noopener");
      }
      return;
    }

    if (tag === "a" && (name === "target" || name === "rel")) {
      return;
    }

    if (tag === "font" && (name === "face" || name === "color" || name === "size")) {
      return;
    }

    element.removeAttribute(attribute.name);
  });
}

function sanitizeNode(node) {
  const childNodes = [...node.childNodes];

  childNodes.forEach((child) => {
    if (child.nodeType === window.Node.COMMENT_NODE) {
      child.remove();
      return;
    }

    if (child.nodeType === window.Node.TEXT_NODE) {
      return;
    }

    if (child.nodeType !== window.Node.ELEMENT_NODE) {
      child.remove();
      return;
    }

    const tag = child.tagName.toLowerCase();

    if (REMOVE_WITH_CONTENT_TAGS.has(tag)) {
      child.remove();
      return;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      unwrapNode(child);
      sanitizeNode(node);
      return;
    }

    sanitizeElementAttributes(child);
    sanitizeNode(child);
  });
}

export function sanitizeRichTextHtml(value = "") {
  const rawValue = String(value || "").trim();
  if (!rawValue || typeof window === "undefined" || typeof DOMParser === "undefined") {
    return rawValue;
  }

  const parser = new DOMParser();
  const documentNode = parser.parseFromString(`<div>${rawValue}</div>`, "text/html");
  const wrapper = documentNode.body.firstElementChild;

  if (!wrapper) {
    return "";
  }

  sanitizeNode(wrapper);

  return wrapper.innerHTML
    .replace(/<(div|p)>\s*(<br\s*\/?>)?\s*<\/(div|p)>/gi, "")
    .trim();
}
